'use client'

import { Button } from '@repo/design-system-cf/components/button'
import { IconCheckCircle, IconFileDownload } from '@repo/design-system-cf/components/icons'
import { useTranslations } from 'next-intl'
import { LocalizedPrice } from '@repo/web-ui/components/localized-price/localized-price'
import * as React from 'react'
import { Typography } from '@repo/design-system-cf/components/typography'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { Link } from '@repo/design-system-cf/components/link'
import dynamic from 'next/dynamic'

import { SaveToMyDownloadButton } from './save-to-my-download-button'
import { useIsLoggedInQuery } from 'auth/hooks'
import { useUserAuthModal } from '@repo/user-auth-dialog/user-auth-dialog-context'
import { useDownloadLegacyProduct } from '@repo/web-ui/hooks/use-download-legacy-product'
import * as Sentry from '@sentry/nextjs'
import toast from 'react-hot-toast'

const TryForFreeModal = dynamic(
  () => import('@web/components/prod/try-for-free-modal').then(module => module.TryForFreeModal),
  {
    ssr: false,
  },
)

export const FreeDownloadButton = ({ price, pid }: { price?: number; pid: string }) => {
  const [isProductDownloaded, setIsProductDownloaded] = React.useState(false)
  const t = useTranslations('pages.daily-gifts.categories')
  const { data: isLoggedIn } = useIsLoggedInQuery()
  const loginModal = useUserAuthModal()
  const { download, isLoading } = useDownloadLegacyProduct({ type: 'daily-gift' })
  const [isTryForFreeModalOpen, setIsTryForFreeModalOpen] = React.useState(false)

  const handleOnDownload = () => {
    if (!isLoggedIn) {
      loginModal.openModal('register')
      return
    }

    try {
      download({
        productId: pid,
        isFree: true,
        onSuccess: () => {
          setIsProductDownloaded(true)
          setIsTryForFreeModalOpen(true)
        },
      })
    } catch (error) {
      Sentry.captureException(error)
      toast.error(t('downloaded_error'))
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <div>
        <Button
          variant="primary-green-bright"
          className="mt-3"
          fullWidth
          iconLeft={isProductDownloaded ? <IconCheckCircle /> : <IconFileDownload />}
          onClick={handleOnDownload}
          disabled={isLoading}
          loading={isLoading}
        >
          <span className="uppercase font-bold">
            {isProductDownloaded ? t('downloaded') : t('free_download')}
          </span>
          {isProductDownloaded ? null : (
            <span className="line-through ml-2 font-medium">
              <LocalizedPrice value={price} />
            </span>
          )}
        </Button>
      </div>
      <div className="flex flex-col gap-2 md:hidden">
        <div className="h-1 border-b border-cf-grey-100 mb-1" />
        <SaveToMyDownloadButton pid={pid} />
        <Typography variant={'cf-label-02'}>
          {t.rich('save_info', {
            link: text => (
              <Link size={'small'} href={AppRoutes.LICENSE_KEYS_OVERVIEW} variant={'primary-green'}>
                {text}
              </Link>
            ),
          })}
        </Typography>
      </div>
      <TryForFreeModal
        isOpen={isTryForFreeModalOpen}
        onClose={() => setIsTryForFreeModalOpen(false)}
      />
    </div>
  )
}
